
import { useState } from "react";
import { Separator } from "@/components/ui/separator";
import { SEOKnowledgeItem } from "@/types";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';

interface SEOArticleProps {
  article: SEOKnowledgeItem;
}

const SEOArticle = ({ article }: SEOArticleProps) => {
  const [expandedSections, setExpandedSections] = useState<number[]>([0]);

  const toggleSection = (index: number) => {
    setExpandedSections(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index) 
        : [...prev, index]
    );
  };

  const renderChart = (chartData: SEOKnowledgeItem["content"][0]["chartData"]) => {
    if (!chartData) return null;
    
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];
    
    switch(chartData.type) {
      case 'bar':
        return (
          <div className="my-6">
            <h5 className="text-center font-medium mb-4">{chartData.title}</h5>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value">
                  {chartData.data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      case 'pie':
        return (
          <div className="my-6">
            <h5 className="text-center font-medium mb-4">{chartData.title}</h5>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        );
      case 'line':
        return (
          <div className="my-6">
            <h5 className="text-center font-medium mb-4">{chartData.title}</h5>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="value" stroke="#8884d8" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <Separator className="my-4" />
      
      <div className="space-y-8">
        {article.content.map((section, index) => (
          <div key={index} className="border border-border rounded-lg overflow-hidden">
            <div 
              className="flex justify-between items-center p-4 bg-secondary/20 cursor-pointer"
              onClick={() => toggleSection(index)}
            >
              <h4 className="text-lg font-semibold">{section.title}</h4>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                {expandedSections.includes(index) ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </div>
            
            {expandedSections.includes(index) && (
              <div className="p-4">
                <div className="space-y-4 text-foreground/80 leading-relaxed">
                  {section.content.split('\n\n').map((paragraph, idx) => {
                    // Process highlights for this paragraph if they exist
                    let processedParagraph = paragraph;
                    if (section.highlights) {
                      section.highlights.forEach(highlight => {
                        // Replace the highlighted text with a bold version
                        // Use a regex that matches the exact phrase but is case-insensitive
                        const regex = new RegExp(`(${highlight})`, 'gi');
                        processedParagraph = processedParagraph.replace(regex, '<strong>$1</strong>');
                      });
                      return <p key={idx} dangerouslySetInnerHTML={{ __html: processedParagraph }} />;
                    }
                    return <p key={idx}>{paragraph}</p>;
                  })}
                </div>

                {section.chartData && renderChart(section.chartData)}
                
                {section.tips && (
                  <div className="bg-desert-cactus/5 border-l-4 border-desert-cactus p-4 mt-4 rounded-r-md">
                    <h5 className="font-semibold text-desert-cactus mb-2">Pro Tips</h5>
                    <ul className="list-disc list-inside space-y-2">
                      {section.tips.map((tip, tipIdx) => (
                        <li key={tipIdx}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {article.resources && (
        <div className="mt-8 pt-4 border-t">
          <h4 className="font-semibold mb-2">Additional Resources</h4>
          <ul className="list-disc list-inside space-y-1">
            {article.resources.map((resource, idx) => (
              <li key={idx}>
                <a 
                  href={resource.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-desert-sunset hover:underline"
                >
                  {resource.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SEOArticle;
